import React, { useMemo } from 'react'
import { useParams, useSearchParams, useLocation } from 'react-router-dom'
import MapComponent from 'components/map'
import { LocationProps, MarkerProps } from 'types/CommonTypes'
import { getMarker } from 'utils/map'
import './index.css'
import { CarpoolType, DefaultCarpoolType } from 'types/CommunityTypes'
import { getCommunityCarpoolTypeLabel } from 'utils/communities'
import { isDateValid } from 'utils/constants'
import { StudentProps } from 'types/StudentTypes'
import { UserOutputProps } from 'types/UserTypes'
import BackComponent from 'components/back'

// Minimal scaffold for Live Map view. Reuses Leaflet MapComponent used in Dashboard.
// Accepts optional lat/lng via search params for now. Later, will fetch driver live location by pool params.

const CarpoolLiveTrackingComponent: React.FC<{ user?: UserOutputProps; students: StudentProps[] }> = ({
  user,
  students,
}) => {
  const params = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // const poolDriverId: string = location.state?.userId ?? params?.poolDriverId ?? ''
  const fromPage: string = location.state?.fromPage ?? ''
  const poolType: CarpoolType | undefined = params?.poolType
  const poolDate: string | undefined = params?.poolDate
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = (students ?? []).find((s: StudentProps) => s.studentId === studentId)

  // optional driver coords if provided (still supported)
  const lat = parseFloat(searchParams.get('lat') || '')
  const lng = parseFloat(searchParams.get('lng') || '')
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng)

  const centerLocation: LocationProps | undefined = useMemo(() => {
    if (!student) return undefined
    const isAdminView = fromPage?.toLowerCase().includes('admin')
    if (isAdminView) return student.schoolLocation
    if (poolType === DefaultCarpoolType.ARRIVAL) return student.pickupLocation ?? student.schoolLocation
    return student.dropoffLocation ?? student.schoolLocation
  }, [student, fromPage, poolType])

  const centerMarker: MarkerProps = useMemo(() => {
    // If explicit lat/lng present, still honor as a live position marker for centering
    if (hasCoords) return getMarker({ address: '', lat, lng }, 'Live')
    if (centerLocation?.lat && centerLocation?.lng) return getMarker(centerLocation, 'Live')
    // Default fallback center (neutral)
    return getMarker({ address: '', lat: 37.7749, lng: -122.4194 }, 'Live')
  }, [hasCoords, lat, lng, centerLocation])

  return (
    <div className='w-full h-full flex flex-col bg-gray-50'>
      {/* Header using shared BackComponent for consistency */}
      <div className='border-b border-gray-200 bg-white'>
        <BackComponent
          text='Live Location'
          rightElement={
            <div className='flex items-center gap-2'>
              {poolType && (
                <span className='text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700'>
                  {getCommunityCarpoolTypeLabel(poolType)}
                </span>
              )}
              {poolDate && isDateValid(poolDate) && (
                <span className='text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700'>{poolDate}</span>
              )}
              <span className='text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700'>
                {hasCoords ? 'Live' : 'Waiting'}
              </span>
            </div>
          }
        >
          <div className='px-5 pb-2 -mt-2'>
            <div className='text-[11px] text-gray-600'>
              {fromPage?.toLowerCase().includes('admin')
                ? 'Admin view: See drivers approaching pickup at school. Location ends after pickup.'
                : 'Family view: See your driver after pickup until dropoff. Location ends after dropoff.'}
            </div>
          </div>
        </BackComponent>
      </div>

      {/* Map and legend */}
      {!hasCoords && (
        <div className='px-3 py-2 text-xs text-gray-600'>Waiting for the driver to start sharing location…</div>
      )}

      <div className='p-3 pt-2 flex-1 flex flex-col gap-2'>
        <div className='rounded-lg border border-gray-200 bg-white overflow-hidden'>
          <MapComponent centerMarker={centerMarker} defaultZoom={hasCoords ? 14 : 11} />
        </div>
        <div className='flex items-center gap-3 text-[11px] text-gray-600'>
          <div className='flex items-center gap-1'>
            <span className='inline-block w-2.5 h-2.5 rounded-full bg-[#5F6D7E]'></span>
            <span>Driver location</span>
          </div>
          <div className='text-gray-400'>•</div>
          <div>Updates periodically while sharing is on</div>
        </div>
      </div>
    </div>
  )
}

export default function Index({ user, students }: { user?: UserOutputProps; students: StudentProps[] }): JSX.Element {
  return <CarpoolLiveTrackingComponent user={user} students={students} />
}
